// src/hooks/useDashboardData.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Colors } from "../constants/Colors";

const MAX_ROWS = 50;
const DAY = 24 * 60 * 60 * 1000;

export const RINCONADA_BOUNDS = {
  minLat: 13.25, maxLat: 13.65,
  minLng: 123.15, maxLng: 123.65,
};

export const STATUS_COLORS = {
  Active:   Colors.STATUS_ACTIVE,
  Busy:     Colors.STATUS_BUSY,
  Inactive: Colors.STATUS_INACTIVE,
  Offline:  Colors.STATUS_OFFLINE,
  default:  Colors.SECONDARY_BLUE,
};

const extractArray = (res) => {
  if (!res || res.status !== "fulfilled") return [];
  const d = res.value?.data;
  if (Array.isArray(d?.data?.data)) return d.data.data;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d)) return d;
  return [];
};

const safeDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const pctDelta = (curr, prev) => {
  if (prev === 0) return curr > 0 ? 100 : 0;
  return Math.round(((curr - prev) / prev) * 100);
};

const buildSparkline = (rentals, status, buckets) => {
  const now = Date.now();
  const bucketMs = (30 * DAY) / buckets;
  const out = new Array(buckets).fill(0);
  rentals.forEach((r) => {
    if (r.status !== status) return;
    const t = safeDate(r.created_at || r.pickup_date || r.createdAt);
    if (!t) return;
    const idx = buckets - 1 - Math.floor((now - t.getTime()) / bucketMs);
    if (idx >= 0 && idx < buckets) out[idx] += 1;
  });
  return out;
};

export function useDashboardData(range = "30d") {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [raw, setRaw] = useState({
    passengers: [], operators: [], jeepneys: [], rentals: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login", { replace: true }); return; }

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      try {
        setLoading(true); setError(null);
        const responses = await Promise.allSettled([
          api.get(`/admin/passengers?per_page=${MAX_ROWS}`, { signal: controller.signal }),
          api.get(`/admin/operators?per_page=${MAX_ROWS}`, { signal: controller.signal }),
          api.get(`/admin/jeepneys?per_page=${MAX_ROWS}`, { signal: controller.signal }),
          api.get(`/admin/rentals?per_page=${MAX_ROWS}`, { signal: controller.signal }),
        ]);
        if (cancelled) return;
        setRaw({
          passengers: extractArray(responses[0]),
          operators: extractArray(responses[1]),
          jeepneys: extractArray(responses[2]),
          rentals: extractArray(responses[3]),
        });
      } catch (err) {
        if (cancelled || err.name === "CanceledError" || err.name === "AbortError") return;
        if (err.response?.status === 401) { navigate("/login", { replace: true }); return; }
        setError(err.response?.data?.message || err.message || "Failed to load dashboard data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; controller.abort(); };
  }, [user, authLoading, navigate]);

  const analytics = useMemo(() => {
    const { passengers, operators, jeepneys, rentals } = raw;
    const now = Date.now();

    const rangeMs =
      range === "7d"  ? 7 * DAY :
      range === "90d" ? 90 * DAY :
      range === "ytd" ? now - new Date(new Date().getFullYear(), 0, 1).getTime() :
                        30 * DAY;

    const inWindow = (r, offsetMs = 0) => {
      const t = safeDate(r.created_at || r.pickup_date || r.createdAt);
      if (!t) return false;
      const ts = t.getTime();
      return ts >= now - rangeMs - offsetMs && ts < now - offsetMs;
    };

    const activeJeepneys = jeepneys.filter(
      (j) => String(j.status || "").toLowerCase() === "active"
    ).length;

    const ongoingTrips = rentals.filter((r) =>
      ["In Progress", "Confirmed"].includes(r.status)
    ).length;

    const completedNow  = rentals.filter((r) => r.status === "Completed" && inWindow(r)).length;
    const completedPrev = rentals.filter((r) => r.status === "Completed" && inWindow(r, rangeMs)).length;
    const pendingNow    = rentals.filter((r) => r.status === "Pending"   && inWindow(r)).length;
    const pendingPrev   = rentals.filter((r) => r.status === "Pending"   && inWindow(r, rangeMs)).length;

    const usersNow = passengers.length + operators.length;

    const stats = [
      {
        key: "users",
        title: "Total Users",
        value: usersNow,
        subtitle: `${operators.length} operators · ${passengers.length} passengers`,
        icon: "bi-people-fill",
        color: Colors.SECONDARY_BLUE,
        delta: 0,
        spark: [],
      },
      {
        key: "jeepneys",
        title: "Active Jeepneys",
        value: activeJeepneys,
        subtitle: `${Math.max(0, jeepneys.length - activeJeepneys)} inactive`,
        icon: "bi-truck-front-fill",
        color: Colors.STATUS_INACTIVE,
        delta: 0,
        spark: [],
      },
      {
        key: "completed",
        title: "Completed Trips",
        value: completedNow,
        subtitle: `${ongoingTrips} ongoing`,
        icon: "bi-check-circle-fill",
        color: Colors.SUCCESS,
        delta: pctDelta(completedNow, completedPrev),
        spark: buildSparkline(rentals, "Completed", 12),
      },
      {
        key: "pending",
        title: "Pending Requests",
        value: pendingNow,
        subtitle: "Awaiting action",
        icon: "bi-clock-history",
        color: Colors.PRIMARY,
        delta: pctDelta(pendingNow, pendingPrev),
        spark: buildSparkline(rentals, "Pending", 12),
      },
    ];

    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const activity = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(new Date().getFullYear(), new Date().getMonth() - i, 1);
      const m = d.getMonth(), y = d.getFullYear();
      const inMonth = rentals.filter((r) => {
        const t = safeDate(r.created_at || r.pickup_date || r.createdAt);
        return t && t.getMonth() === m && t.getFullYear() === y;
      });
      activity.push({
        month: months[m],
        active:    inMonth.filter((r) => ["Confirmed", "In Progress"].includes(r.status)).length,
        completed: inMonth.filter((r) => r.status === "Completed").length,
        pending:   inMonth.filter((r) => r.status === "Pending").length,
        cancelled: inMonth.filter((r) => r.status === "Cancelled").length,
        total:     inMonth.length,
      });
    }

    const dist = {
      total: rentals.length,
      confirmed: rentals.filter((r) => ["Confirmed", "In Progress"].includes(r.status)).length,
      completed: rentals.filter((r) => r.status === "Completed").length,
      pending:   rentals.filter((r) => r.status === "Pending").length,
      cancelled: rentals.filter((r) => r.status === "Cancelled").length,
    };

    const opMap = new Map();
    rentals.forEach((r) => {
      if (!inWindow(r) || r.status !== "Completed") return;
      const id = r.operator?.operator_id || r.operator_id || r.operator?.id;
      if (!id) return;
      const name = `${r.operator?.first_name || ""} ${r.operator?.last_name || ""}`.trim()
        || r.operator_name || `Operator #${id}`;
      const cur = opMap.get(id) || { id, name, trips: 0, revenue: 0 };
      cur.trips += 1;
      cur.revenue += Number(r.fare) || 0;
      opMap.set(id, cur);
    });
    const topOperators = [...opMap.values()]
      .sort((a, b) => b.trips - a.trips)
      .slice(0, 5);

    const recentRentals = [...rentals]
      .map((r) => ({
        ...r,
        _date: safeDate(r.created_at || r.pickup_date || r.createdAt),
      }))
      .filter((r) => r._date)
      .sort((a, b) => b._date - a._date)
      .slice(0, 6);

    const locations = operators
      .filter((op) => op.latitude != null && op.longitude != null)
      .map((op) => {
        const lat = parseFloat(op.latitude);
        const lng = parseFloat(op.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
        const inBounds =
          lat >= RINCONADA_BOUNDS.minLat && lat <= RINCONADA_BOUNDS.maxLat &&
          lng >= RINCONADA_BOUNDS.minLng && lng <= RINCONADA_BOUNDS.maxLng;
        const lastUpdate = safeDate(op.last_location_update);
        return {
          id: op.operator_id || op.id,
          name: `${op.first_name || ""} ${op.last_name || ""}`.trim() || "Unknown",
          status: op.status || "Unknown",
          email: op.email || "—",
          phone: op.phone || "—",
          latitude: lat, longitude: lng,
          lastLocationUpdate: lastUpdate,
          isStale: lastUpdate ? lastUpdate.getTime() < now - DAY : true,
          isOutsideBounds: !inBounds,
        };
      })
      .filter(Boolean);

    return {
      stats, activity, dist, topOperators, recentRentals, locations, rentals,
    };
  }, [raw, range]);

  return { ...analytics, raw, loading, error, setError };
}